import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useLocation, useNavigate, useParams } from 'react-router';
import type { GroceryItemDto, GroceryListDetail } from '@fewerbags/schema';
import { Alert } from '../../components/Alert';
import { AppShell } from '../../components/AppShell';
import { Button } from '../../components/Button';
import { Dialog } from '../../components/Dialog';
import { EmptyState } from '../../components/EmptyState';
import { PlusIcon } from '../../components/icons';
import { GroceriesIcon } from '../../components/NavIcons';
import { ReportDialog } from '../../components/ReportDialog';
import { Section } from '../../components/Section';
import { Skeleton } from '../../components/Skeleton';
import { AddGroceryItemDialog } from '../../components/groceries/AddGroceryItemDialog';
import { ChangeStoreDialog } from '../../components/groceries/ChangeStoreDialog';
import { CheckOffSheet } from '../../components/groceries/CheckOffSheet';
import { EditGroceryItemDialog } from '../../components/groceries/EditGroceryItemDialog';
import { GroceryItemRow } from '../../components/groceries/GroceryItemRow';
import { HaveItSheet } from '../../components/groceries/HaveItSheet';
import { RegenerateListDialog } from '../../components/groceries/RegenerateListDialog';
import { ScanReceiptButton } from '../../components/groceries/ScanReceiptButton';
import { StoreDetectionBanner } from '../../components/groceries/StoreDetectionBanner';
import { StoreNameLabel } from '../../components/groceries/StoreNameLabel';
import { api, queryKeys } from '../../lib/api';
import { messageForError } from '../../lib/errors';
import { formatDayShort } from '../../lib/planDates';
import {
  formatPriceCents,
  groupByDepartment,
  isChecked,
  locationLabel,
  reportableValues,
  sortCheckedItems,
  STATUS_LABEL,
  STATUS_ORDER,
} from './display';
import { groceryKeys } from './queries';
import { useGroceryEvents } from './useGroceryEvents';

export function GroceryListDetailPage() {
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const toast = (location.state as { toast?: string } | null)?.toast;
  const qc = useQueryClient();

  const [name, setName] = useState('');
  const [addOpen, setAddOpen] = useState(false);
  const [changeStoreOpen, setChangeStoreOpen] = useState(false);
  const [regenerateOpen, setRegenerateOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [checkingItem, setCheckingItem] = useState<GroceryItemDto | null>(null);
  const [havingItem, setHavingItem] = useState<GroceryItemDto | null>(null);
  const [editingItem, setEditingItem] = useState<GroceryItemDto | null>(null);
  const [reportingItem, setReportingItem] = useState<GroceryItemDto | null>(null);
  const [cartOpen, setCartOpen] = useState(false);

  const detail = useQuery({
    queryKey: groceryKeys.detail(id),
    queryFn: () => api.groceries.get(id),
    enabled: id !== '',
  });
  // Receipt scanning shares the photo reader's AI key (D34).
  const serverConfig = useQuery({
    queryKey: queryKeys.authConfig,
    queryFn: () => api.auth.config(),
    staleTime: 5 * 60_000,
  });

  // Set while *this* tab is deleting the list, so its own `list.deleted` event
  // doesn't also bounce it to the index with the "someone else" notice.
  const deletingRef = useRef(false);
  useGroceryEvents(id, id !== '', {
    onListDeleted: () => {
      if (deletingRef.current) return;
      void qc.invalidateQueries({ queryKey: groceryKeys.all });
      qc.removeQueries({ queryKey: groceryKeys.detail(id) });
      void navigate('/groceries', {
        replace: true,
        state: { toast: 'That list was deleted by someone in your household.' },
      });
    },
  });

  useEffect(() => {
    if (detail.data) setName(detail.data.list.name);
  }, [detail.data?.list.name]);

  const detailKey = groceryKeys.detail(id);
  const patchDetail = (updater: (d: GroceryListDetail) => GroceryListDetail) => {
    qc.setQueryData<GroceryListDetail>(detailKey, (old) => (old ? updater(old) : old));
  };

  const rename = useMutation({
    mutationFn: () => api.groceries.update(id, { name }),
    onSuccess: (list) => patchDetail((d) => ({ ...d, list })),
  });
  const setStatus = useMutation({
    mutationFn: (status: (typeof STATUS_ORDER)[number]) => api.groceries.update(id, { status }),
    onSuccess: (list) => patchDetail((d) => ({ ...d, list })),
  });
  const removeList = useMutation({
    mutationFn: () => {
      deletingRef.current = true;
      return api.groceries.remove(id);
    },
    onError: () => {
      deletingRef.current = false;
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: groceryKeys.all });
      qc.removeQueries({ queryKey: detailKey });
      void navigate('/groceries', { replace: true });
    },
  });
  const uncheck = useMutation({
    mutationFn: (item: GroceryItemDto) => api.groceries.items.uncheck(id, item.id, item.purchaseId),
    onMutate: async (item) => {
      await qc.cancelQueries({ queryKey: detailKey });
      const previous = qc.getQueryData<GroceryListDetail>(detailKey);
      patchDetail((d) => ({
        ...d,
        list: { ...d.list, checkedCount: Math.max(0, d.list.checkedCount - 1) },
        items: d.items.map((i) =>
          i.id === item.id
            ? { ...i, checkedAt: null, checkedByName: null, haveAt: null, pantryItemId: null }
            : i,
        ),
      }));
      return { previous };
    },
    onError: (_err, _item, ctx) => {
      if (ctx?.previous) qc.setQueryData(detailKey, ctx.previous);
      // e.g. 409 CHECK_CHANGED: another shopper re-checked it — show what the server has now.
      void qc.invalidateQueries({ queryKey: detailKey });
    },
    onSuccess: (updated) => {
      patchDetail((d) => ({
        ...d,
        items: d.items.map((i) => (i.id === updated.id ? updated : i)),
      }));
    },
  });
  const removeItem = useMutation({
    mutationFn: (item: GroceryItemDto) => api.groceries.items.remove(id, item.id),
    onSuccess: (_res, item) => {
      patchDetail((d) => ({
        ...d,
        list: { ...d.list, itemCount: Math.max(0, d.list.itemCount - 1) },
        items: d.items.filter((i) => i.id !== item.id),
      }));
    },
  });

  if (id === '' || detail.isPending) {
    return (
      <AppShell>
        <Skeleton lines={4} />
      </AppShell>
    );
  }
  if (detail.isError || !detail.data) {
    return (
      <AppShell title="Grocery list">
        <Alert tone="error">{messageForError(detail.error)}</Alert>
        <Link to="/groceries" className="mt-3 inline-block text-sm text-brand underline">
          Back to lists
        </Link>
      </AppShell>
    );
  }

  const { list, store, items } = detail.data;
  const uncheckedGroups = groupByDepartment(items.filter((i) => !isChecked(i)));
  const checkedItems = sortCheckedItems(items.filter(isChecked));
  const nameDirty = name.trim() !== '' && name !== list.name;

  return (
    <AppShell>
      <div className="flex flex-col gap-4">
        {toast ? <Alert tone="success">{toast}</Alert> : null}
        <div className="card relative overflow-hidden p-4 sm:p-5">
          <span
            aria-hidden
            className="absolute inset-x-0 top-0 h-1.5 bg-gradient-to-r from-brand to-accent"
          />
          <Link
            to="/groceries"
            className="inline-flex min-h-9 items-center text-sm font-medium text-brand underline-offset-2 hover:underline"
          >
            ← Back to lists
          </Link>
          <div className="mt-1 flex flex-wrap items-center gap-2">
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              aria-label="List name"
              className="min-h-11 min-w-0 flex-1 rounded-lg border border-line/60 bg-surface px-2 text-xl font-bold text-brand sm:text-2xl"
            />
            {nameDirty ? (
              <Button block={false} onClick={() => rename.mutate()} disabled={rename.isPending}>
                Save
              </Button>
            ) : null}
          </div>
          {rename.isError ? <Alert tone="error">{messageForError(rename.error)}</Alert> : null}
          {uncheck.isError ? <Alert tone="error">{messageForError(uncheck.error)}</Alert> : null}
          <p className="mt-1 flex flex-wrap items-center gap-x-2 text-sm text-muted">
            {list.dateFrom && list.dateTo ? (
              <span>
                {formatDayShort(list.dateFrom)} – {formatDayShort(list.dateTo)}
              </span>
            ) : null}
            {store ? (
              <>
                <span aria-hidden>·</span>
                <StoreNameLabel storeId={store.id} />
              </>
            ) : null}
            <span aria-hidden>·</span>
            <span>
              {list.checkedCount}/{list.itemCount} checked
            </span>
          </p>
          <span aria-hidden className="mt-2 block h-2 overflow-hidden rounded-full bg-brand-soft">
            <span
              className="block h-full rounded-full bg-gradient-to-r from-brand to-accent transition-[width] duration-500"
              style={{
                width: `${list.itemCount ? Math.round((list.checkedCount / list.itemCount) * 100) : 0}%`,
              }}
            />
          </span>

          <div className="mt-2 flex flex-wrap gap-2" role="group" aria-label="List status">
            {STATUS_ORDER.map((s) => (
              <button
                key={s}
                type="button"
                aria-pressed={list.status === s}
                disabled={setStatus.isPending}
                onClick={() => s !== list.status && setStatus.mutate(s)}
                className={`min-h-9 rounded-full border px-3.5 text-sm font-medium transition-colors disabled:opacity-50 ${
                  list.status === s
                    ? 'border-brand bg-brand text-white shadow-sm'
                    : 'border-line bg-surface text-ink hover:bg-brand-soft'
                }`}
              >
                {STATUS_LABEL[s]}
              </button>
            ))}
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          <Button block={false} onClick={() => setAddOpen(true)}>
            <PlusIcon className="size-4" /> Add item
          </Button>
          <Button variant="secondary" block={false} onClick={() => setChangeStoreOpen(true)}>
            Change store
          </Button>
          <Button variant="secondary" block={false} onClick={() => setRegenerateOpen(true)}>
            Regenerate
          </Button>
          {serverConfig.data?.features.photoImport &&
          (list.status === 'open' || list.status === 'shopping') ? (
            <ScanReceiptButton listId={id} />
          ) : null}
        </div>

        <StoreDetectionBanner listId={id} status={list.status} currentStoreId={list.storeId} />

        {uncheckedGroups.length === 0 && checkedItems.length === 0 ? (
          <div className="card">
            <EmptyState
              accent="groceries"
              icon={<GroceriesIcon className="size-full" />}
              title="Nothing on this list yet"
              action={
                <Button block={false} onClick={() => setAddOpen(true)}>
                  <PlusIcon className="size-4" /> Add item
                </Button>
              }
            />
          </div>
        ) : null}

        {uncheckedGroups.map((group) => (
          <section key={group.key} aria-labelledby={`grocery-group-${group.key}`}>
            <h2
              id={`grocery-group-${group.key}`}
              className="mb-2 flex items-center gap-2 text-base font-semibold text-brand"
            >
              <span
                aria-hidden
                className="h-4 w-1.5 rounded-full bg-gradient-to-b from-brand to-accent"
              />
              {group.label}{' '}
              <span className="rounded-full bg-brand-soft px-2 text-xs font-semibold text-brand">
                {group.items.length}
              </span>
            </h2>
            <ul className="flex flex-col gap-2">
              {group.items.map((item) => (
                <GroceryItemRow
                  key={item.id}
                  item={item}
                  onToggle={(i) => (isChecked(i) ? uncheck.mutate(i) : setCheckingItem(i))}
                  onHave={setHavingItem}
                  onEdit={setEditingItem}
                  onReport={setReportingItem}
                  storeId={list.storeId}
                  onRemove={(i) => {
                    if (confirm(`Remove ${i.ingredientName} from the list?`)) removeItem.mutate(i);
                  }}
                  busy={uncheck.isPending || removeItem.isPending}
                />
              ))}
            </ul>
          </section>
        ))}

        {checkedItems.length > 0 ? (
          <section aria-labelledby="grocery-group-cart">
            <button
              type="button"
              onClick={() => setCartOpen((o) => !o)}
              aria-expanded={cartOpen}
              id="grocery-group-cart"
              className="flex min-h-11 w-full items-center justify-between rounded-xl border border-line bg-accent-soft/60 px-4 text-sm font-semibold text-accent-ink transition-colors hover:bg-accent-soft"
            >
              <span>In cart ({checkedItems.length})</span>
              <span aria-hidden>{cartOpen ? '▲' : '▼'}</span>
            </button>
            {cartOpen ? (
              <ul className="mt-2 flex flex-col gap-2">
                {checkedItems.map((item) => (
                  <GroceryItemRow
                    key={item.id}
                    item={item}
                    onToggle={(i) => (isChecked(i) ? uncheck.mutate(i) : setCheckingItem(i))}
                    onEdit={setEditingItem}
                    onRemove={(i) => {
                      if (confirm(`Remove ${i.ingredientName} from the list?`))
                        removeItem.mutate(i);
                    }}
                    busy={uncheck.isPending || removeItem.isPending}
                  />
                ))}
              </ul>
            ) : null}
          </section>
        ) : null}

        <Section title="Danger zone" accent="neutral">
          {removeList.isError ? (
            <Alert tone="error">{messageForError(removeList.error)}</Alert>
          ) : null}
          <Button variant="danger" onClick={() => setConfirmDelete(true)}>
            Delete this list
          </Button>
        </Section>
      </div>

      <AddGroceryItemDialog open={addOpen} onClose={() => setAddOpen(false)} listId={id} />
      <ChangeStoreDialog
        open={changeStoreOpen}
        onClose={() => setChangeStoreOpen(false)}
        listId={id}
        currentStoreId={list.storeId}
      />
      <RegenerateListDialog
        open={regenerateOpen}
        onClose={() => setRegenerateOpen(false)}
        list={list}
      />
      <CheckOffSheet
        open={checkingItem !== null}
        onClose={() => setCheckingItem(null)}
        listId={id}
        item={checkingItem}
        storeId={list.storeId}
      />
      <HaveItSheet
        open={havingItem !== null}
        onClose={() => setHavingItem(null)}
        listId={id}
        item={havingItem}
      />
      <ReportDialog
        open={reportingItem !== null}
        onClose={() => setReportingItem(null)}
        kind="store_product_price"
        title="Report shared store info"
        description={
          reportingItem
            ? `Other shoppers' info for ${reportingItem.ingredientName} at this store.`
            : undefined
        }
        choices={
          reportingItem
            ? reportableValues(reportingItem, list.storeId).map((k) => ({
                value: k,
                label:
                  k === 'price'
                    ? `Price ${formatPriceCents(reportingItem.priceCents) ?? ''}`.trim()
                    : `Location ${locationLabel(reportingItem.location) ?? ''}`.trim(),
              }))
            : []
        }
        send={(input) =>
          api.stores.reportProduct(list.storeId!, reportingItem!.productId!, {
            kind: input.choice === 'location' ? 'location' : 'price',
            reason: input.reason,
            ...(input.note ? { note: input.note } : {}),
          })
        }
      />
      <EditGroceryItemDialog
        open={editingItem !== null}
        onClose={() => setEditingItem(null)}
        listId={id}
        item={editingItem}
      />

      {confirmDelete ? (
        <Dialog open onClose={() => setConfirmDelete(false)} title="Delete this list?">
          <p className="text-sm text-ink">This removes the whole list, including checked items.</p>
          <div className="flex gap-2">
            <Button
              variant="danger"
              disabled={removeList.isPending}
              onClick={() => {
                setConfirmDelete(false);
                removeList.mutate();
              }}
            >
              Delete it
            </Button>
            <Button variant="secondary" onClick={() => setConfirmDelete(false)}>
              Keep it
            </Button>
          </div>
        </Dialog>
      ) : null}
    </AppShell>
  );
}
